import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '@environments/environment';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { catchError, Observable, throwError } from 'rxjs';

const AUTOLOGIN_TOKEN = 'autologin_token';

@Injectable({
  providedIn: 'root',
})
export class HttpApiService {
  /**
   * AUTH_TOKEN  : It is a private key, and must be set in http header only when login or signup
   * ACCESS_TOKEN: It is a communication API key, and save it in local storage
   * API_URL     : API Basic URL. Ex Demo: http://preconys1.kiwilab.fr:8080/api/
   */
  private API_URL: string = environment.servers.resources.url || '';

  constructor(
    private http: HttpClient,
    private storage: OAuthStorage,
    private oauthService: OAuthService,
  ) {}

  get(url: string, params?: { [param: string]: string }): Observable<any> {
    return this.http
      .get(this.apiUrl(url), {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  getGeneric<T>(url: string): Observable<T> {
    return this.http
      .get<T>(this.apiUrl(url))
      .pipe(catchError(this.handleError));
  }

  getWithOptions(url: string, options: any = {}): Observable<any> {
    return this.http
      .get(this.apiUrl(url), options)
      .pipe(catchError(this.handleError));
  }

  post(url: string, body?: any): Observable<any> {
    return this.http
      .post(this.apiUrl(url), body)
      .pipe(catchError(this.handleError));
  }

  postWithOptions(url: string, body?: any, options?: any): Observable<any> {
    return this.http
      .post(this.apiUrl(url), body, options)
      .pipe(catchError(this.handleError));
  }

  postFormData(
    url: string,
    body: FormData,
    paramHeaders: any,
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.append('skip', 'true');
    headers = headers.append('X-Engine-Language', 'fr');
    headers = headers.append('Accept', '*/*');

    Object.keys(paramHeaders).forEach(key => {
      headers = headers.append(key.toString(), paramHeaders[key]);
    });

    return this.http
      .post<any>(this.apiUrl(url), body, { headers: headers })
      .pipe(catchError(this.handleError));
  }

  postGeneric<T>(url: string, body?: any): Observable<T> {
    return this.http
      .post<T>(this.apiUrl(url), body)
      .pipe(catchError(this.handleError));
  }

  patch(url: string, body?: any): Observable<any> {
    return this.http.patch(this.apiUrl(url), body);
  }

  put(url: string, body?: any): Observable<any> {
    return this.http
      .put(this.apiUrl(url), body)
      .pipe(catchError(this.handleError));
  }

  delete(url: string, body?: any): Observable<any> {
    return this.http
      .delete(this.apiUrl(url), body)
      .pipe(catchError(this.handleError));
  }

  private apiUrl(url: string): string {
    return this.API_URL + url;
  }
  /**
   * Fonction fléchée, et non méthode : elle est passée par référence à neuf `catchError`, où une
   * méthode classique perdrait son `this`. Les tokens n'étaient donc jamais effacés, et comme un
   * token révoqué n'est pas expiré, le rechargement repartait avec le même token invalide.
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    if (error.status === 401) {
      console.error('Unauthorized error:', error);
      this.storage?.removeItem(AUTOLOGIN_TOKEN);
      this.oauthService?.logOut(true);
      window.location.reload();
    }

    return throwError(error);
  };
}
